import type { ComponentProps } from 'react'
import GuidesOverlay from '@/components/app/document/overlays/guides-overlay'
import type { Guide } from '@/lib/guides/types/guide'
import type { computeSinglePageHorizontalGuideSpan } from '../helpers/spread-geometry'
import type { CenterCanvasConfig } from '../types/center-canvas-config'
import type { DraggingGuide } from '../types/dragging-guide'
import { GuideCreatePreview } from './guide-create-preview'

/**
 * Ruler guides over the document (z-index 4): the guide lines and, while a guide is being dragged
 * out of a ruler, its preview with a live X/Y readout. On single-first/single-last, horizontal
 * guide lines + drag preview span the whole spread (real page + cover/pasteboard side), not just
 * the real page's own width (WMPCM-1944, widened by WMPCM-10229) — matching vertical guides, which
 * already span full spread height unconditionally; ruler ticks stay full-range too.
 */
export function GuidesLayer({
  showGuides,
  isPanInteractionActive,
  guides,
  selectedGuideIds,
  onGuideDragStart,
  rulerUnit,
  snappedSpreadOriginCanvasX,
  verticalRulerZeroAtCanvasY,
  scale,
  singlePageHorizontalGuideSpan,
  suspendPointerInteraction,
  draggingGuide,
}: {
  showGuides: boolean
  isPanInteractionActive: boolean
  guides: Guide[]
  selectedGuideIds: Set<string>
  onGuideDragStart: ComponentProps<typeof GuidesOverlay>['onGuideDragStart']
  rulerUnit: CenterCanvasConfig['rulerUnit']
  snappedSpreadOriginCanvasX: number
  verticalRulerZeroAtCanvasY: number
  scale: number
  singlePageHorizontalGuideSpan: ReturnType<typeof computeSinglePageHorizontalGuideSpan>
  suspendPointerInteraction: boolean
  draggingGuide: DraggingGuide | null
}) {
  return (
    <div className="absolute inset-0" style={{ pointerEvents: 'none', zIndex: 4 }}>
      {showGuides && (
        <div
          style={{
            pointerEvents: isPanInteractionActive ? 'none' : 'auto',
          }}
        >
          <GuidesOverlay
            guides={guides}
            selectedGuideIds={selectedGuideIds}
            onGuideDragStart={onGuideDragStart}
            rulerUnit={rulerUnit}
            horizontalZeroAtCanvasX={snappedSpreadOriginCanvasX}
            verticalZeroAtCanvasY={verticalRulerZeroAtCanvasY}
            scale={scale}
            horizontalGuideSpan={singlePageHorizontalGuideSpan}
            suspendPointerInteraction={suspendPointerInteraction}
          />
        </div>
      )}
      {/* Dragging guide preview (ruler drag to create) + live X/Y readout */}
      {draggingGuide && (
        <GuideCreatePreview
          draggingGuide={draggingGuide}
          scale={scale}
          rulerUnit={rulerUnit}
          snappedSpreadOriginCanvasX={snappedSpreadOriginCanvasX}
          verticalRulerZeroAtCanvasY={verticalRulerZeroAtCanvasY}
          singlePageHorizontalGuideSpan={singlePageHorizontalGuideSpan}
        />
      )}
    </div>
  )
}
